import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Layers, ArrowLeft, Package, AlertTriangle, Clock,
  DollarSign, Activity, PackageCheck, SlidersHorizontal, CalendarRange,
} from 'lucide-react';
import Layout from '../../../components/Layout';
import './InventoryReportCommon.css';

interface ReportCard {
  path: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  accent: string;
}

const CARDS: ReportCard[] = [
  {
    path: '/reports/inventory/stock-summary',
    icon: <Package size={28} strokeWidth={1.6} />,
    title: 'Stock Summary',
    description: 'Current stock levels across all products with quantity on hand, reserved, and available.',
    accent: 'var(--ims-raw-indigo-600)',
  },
  {
    path: '/reports/inventory/low-stock',
    icon: <AlertTriangle size={28} strokeWidth={1.6} />,
    title: 'Low Stock / Reorder Alert',
    description: 'Items at or below reorder level — with shortage quantity and suggested reorder amounts.',
    accent: 'var(--ims-raw-amber-600)',
  },
  {
    path: '/reports/inventory/dead-stock',
    icon: <Clock size={28} strokeWidth={1.6} />,
    title: 'Dead Stock',
    description: 'Products with no movement for a configurable period — identify tied-up capital.',
    accent: 'var(--ims-raw-gray-500)',
  },
  {
    path: '/reports/inventory/valuation',
    icon: <DollarSign size={28} strokeWidth={1.6} />,
    title: 'Stock Valuation',
    description: 'Total inventory value using last-purchase cost per product, broken down by warehouse.',
    accent: 'var(--ims-raw-emerald-600)',
  },
  {
    path: '/reports/inventory/movements',
    icon: <Activity size={28} strokeWidth={1.6} />,
    title: 'Stock Movement Ledger',
    description: 'Full transaction history — IN, OUT, and ADJUSTMENT entries with document references.',
    accent: 'var(--ims-raw-blue-600)',
  },
  {
    path: '/reports/inventory/grn',
    icon: <PackageCheck size={28} strokeWidth={1.6} />,
    title: 'GRN Report',
    description: 'All Goods Received Notes with supplier, warehouse, value, and approval status.',
    accent: 'var(--ims-raw-teal-600, #0d9488)',
  },
  {
    path: '/reports/inventory/adjustments',
    icon: <SlidersHorizontal size={28} strokeWidth={1.6} />,
    title: 'Stock Adjustment Report',
    description: 'Audit trail of all manual stock adjustments with reasons and quantity changes.',
    accent: '#7c3aed',
  },
  {
    path: '/reports/inventory/opening-closing',
    icon: <CalendarRange size={28} strokeWidth={1.6} />,
    title: 'Opening vs Closing Stock',
    description: 'Period-based opening balance, movements (IN/OUT), and closing balance with values.',
    accent: 'var(--ims-raw-red-600)',
  },
];

const InventoryReportsPage: React.FC = () => {
  const navigate    = useNavigate();
  const { t }       = useTranslation();

  return (
    <Layout>
      <div className="irp-page">

        {/* Hero */}
        <div className="irp-hero">
          <div>
            <h1 className="irp-hero-title">
              <Layers size={22} strokeWidth={1.8} />
              {t('inventoryReports.title', 'Inventory Reports')}
            </h1>
            <p className="irp-hero-sub">
              {t('inventoryReports.subtitle', 'Stock levels, movements, valuation, and audit reports')}
            </p>
          </div>
          <div className="irp-hero-actions">
            <button className="irp-back-btn" onClick={() => navigate('/reports')}>
              <ArrowLeft size={14} strokeWidth={2} />
              {t('common.back', 'Back')}
            </button>
          </div>
        </div>

        {/* Report cards grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '1rem',
        }}>
          {CARDS.map(card => (
            <button
              key={card.path}
              onClick={() => navigate(card.path)}
              style={{
                background: 'var(--ims-bg-card)',
                border: '1px solid var(--ims-border)',
                borderRadius: 'var(--ims-radius-lg)',
                boxShadow: 'var(--ims-shadow-sm)',
                padding: '1.5rem',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'box-shadow 0.15s, transform 0.15s',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.boxShadow = 'var(--ims-shadow)';
                (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.boxShadow = 'var(--ims-shadow-sm)';
                (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
              }}
            >
              <div style={{
                width: 52,
                height: 52,
                borderRadius: 12,
                background: `color-mix(in srgb, ${card.accent} 12%, transparent)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: card.accent,
                flexShrink: 0,
              }}>
                {card.icon}
              </div>
              <div>
                <div style={{
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  color: 'var(--ims-text-main)',
                  marginBottom: '0.3rem',
                }}>
                  {card.title}
                </div>
                <div style={{
                  fontSize: '0.82rem',
                  color: 'var(--ims-text-sub)',
                  lineHeight: 1.5,
                }}>
                  {card.description}
                </div>
              </div>
            </button>
          ))}
        </div>

      </div>
    </Layout>
  );
};

export default InventoryReportsPage;
